/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { Grid, ShowMoreContainer, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import Markdown from 'bundles/cms/components/Markdown';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';

import _t from 'i18n!nls/premium-certificate';

type Props = CourseProps & {
  index: number;
  courseTotal?: number;
  isMobileScreen?: boolean;
};

type CourseProps = {
  title?: Maybe<string>;
  whatYouWillLearn?: Maybe<Array<Maybe<string>>>;
  projects?: Maybe<string>;
  description?: Maybe<string>;
  defaultExpanded?: boolean;
};

type LearningObjectivesProps = {
  learningObjectives: Array<Maybe<string>>;
};

const styles: Record<string, Interpolation<Theme>> = {
  rowContainer: () => css`
    margin-top: 1.125rem;
    margin-bottom: 1.5rem;
    ${breakpoints.down('sm')} {
      margin-top: 1.5rem;
    }
  `,
  skillsList: css`
    list-style: none;
    padding-left: 0;
    font-size: 0.875rem;
    line-height: 1.5rem;
  `,
  courseTitle: css`
    font-size: 1rem;
    line-height: 1.875rem;
    font-weight: 700;
    font-family: inherit;
    margin-top: 1rem;
  `,
  contentSectionTitle: css`
    font-size: 0.875rem;
    line-height: 1.5rem;
    font-weight: 700;
    font-family: inherit;
    margin-bottom: 0.375rem;
  `,
  overviewTitle: css`
    font-size: 0.875rem;
    line-height: 1.5rem;
    font-weight: 700;
    font-family: inherit;
    margin-bottom: 0.375rem;
    padding-top: 1rem;
  `,
  certificateCourseContainer: () => css`
    background-color: var(--cds-color-white-0);
    padding: 3.125rem 7.25rem 3rem 5rem;
    margin-top: 0.5rem;
    border: 1px solid var(--cds-color-grey-50);
    border-radius: 6px;

    ${breakpoints.down('sm')} {
      padding: 1rem;
    }
  `,
  courseNumber: () => css`
    color: var(--cds-color-grey-600);
    font-size: 1.25rem;
    line-height: 1.5rem;
    margin-bottom: 0.5rem;
  `,
  checkIcon: css`
    width: 15px;
    height: 12px;
    margin-right: 10px;
  `,
};

const LearningObjectives = ({ learningObjectives }: LearningObjectivesProps) => {
  if (!learningObjectives) {
    return null;
  }
  return (
    <ul css={styles.skillsList} data-test="course-learn">
      {learningObjectives.map((skill) => (
        <li key={skill}>
          <CheckIcon size="small" color="interactive" css={styles.checkIcon} />
          {skill}
        </li>
      ))}
    </ul>
  );
};

const CourseContent = ({ title, whatYouWillLearn, projects, description, defaultExpanded }: CourseProps) => (
  <div data-test="CourseContent">
    <Grid container>
      <Grid item>
        <div>
          <h3 css={styles.courseTitle}>{title}</h3>
        </div>
      </Grid>
    </Grid>

    <Grid container>
      <Grid item>
        {description && (
          <div>
            <h4 css={styles.overviewTitle}> {_t('Overview')}</h4>
            <Markdown source={description} />
          </div>
        )}
      </Grid>
    </Grid>

    <ShowMoreContainer
      variant="silent"
      expandedLabel={_t('SHOW LESS')}
      collapsedLabel={_t('SHOW ALL')}
      defaultExpanded={defaultExpanded}
    >
      <Grid container css={styles.rowContainer} spacing={24}>
        <Grid item md={6}>
          {whatYouWillLearn && (
            <div>
              <h4 css={styles.contentSectionTitle}> {_t('What you will learn')}</h4>
              <LearningObjectives learningObjectives={whatYouWillLearn} />
            </div>
          )}
        </Grid>

        <Grid item md={6}>
          {projects && (
            <div data-test="course-projects">
              <h4 css={styles.contentSectionTitle}> {_t('Project(s)')}</h4>
              <Markdown source={projects} showSvgCheck />
            </div>
          )}
        </Grid>
      </Grid>
    </ShowMoreContainer>
  </div>
);

const CertificateCourse = ({
  title,
  whatYouWillLearn,
  projects,
  description,
  index,
  courseTotal,
  isMobileScreen,
}: Props) => {
  if (!title && !whatYouWillLearn && !projects && !description) {
    return null;
  }
  return (
    <div data-test="CertificateCourse" css={styles.certificateCourseContainer}>
      <Grid container>
        <Grid item>
          <p css={styles.courseNumber}>
            {_t(`Course #{courseNum} of #{courseTotal}`, { courseNum: index + 1, courseTotal })}
          </p>
        </Grid>
      </Grid>
      <CourseContent
        title={title}
        projects={projects}
        whatYouWillLearn={whatYouWillLearn}
        description={description}
        defaultExpanded={index === 0 && !isMobileScreen}
        data-test="course-section"
      />
    </div>
  );
};

export default CertificateCourse;
